import classNames from 'clsx'
import { bool, func, number, oneOf, shape, string } from 'prop-types'

import { PAGE_TYPES } from 'shared/consts'
import PreQuerySuggestionsExtended from 'shared/experiments/components/PreQuerySuggestionsExtended'
import { usePreQuerySuggestionsExperimentEnabledAndNotDefault } from 'shared/experiments/components/PreQuerySuggestionsExtended/helpers'
import {
  EXPERIMENT_VARIATION,
  SEARCH_BAR_INTERACTION,
  SERP_PRE_QUERY_SUGGESTIONS_PER_TENANT_EXPERIMENT,
} from 'shared/experiments/consts'
import { getExperiment } from 'shared/experiments/utils/experiments'
import { displayCurrentOrRebrandingCOMExperimentText } from 'shared/experiments/utils/rebranding'
import { useGetExperimentNamePerTenant } from 'shared/experiments/utils/tenantBasedExperimentsUtils'
import { withI18n } from 'shared/utils/withI18n'
import Form from 'views/components/atoms/Form'
import IconSearch from 'views/components/atoms/Icon/IconSearch'
import InputWithButton from 'views/components/molecules/InputWithButton'
import PreQuerySuggestions from 'views/components/organisms/PreQuerySuggestions'
import { EXCLUDED_PAGE_TYPES } from 'views/components/organisms/PreQuerySuggestions/consts'
import { shouldRenderPqsl } from 'views/components/organisms/PreQuerySuggestions/helpers'
import { getPlaceholderText } from 'views/components/organisms/SearchBox/helper.ts'
import SearchBoxList from 'views/components/organisms/SearchBox/SearchBoxList'
import SearchHistory from 'views/components/organisms/SearchBox/SearchHistory'
import Experiment from 'views/components/utils/optimizely/Experiment'
import { i18nType, publicRuntimeConfigType } from 'views/utils/propTypes'

import './SearchBoxDesktop.scss'

const SearchBoxDesktop = ({
  publicRuntimeConfig: {
    publicConfig: { tenant },
    pageProperties: { experiments },
  },
  i18n,
  query,
  inputId,
  displayResults,
  products,
  redirectData,
  handleInputUpdate,
  handleClear,
  handleKeyUp,
  handleSubmit,
  focusedIndex,
  displayedValue,
  formAction,
  handleShowAll,
  handleItemClick,
  hideResults,
  handleValueChange,
  searchHistory,
  handleClearHistory,
  pageType,
  withHighlightedProduct,
  querySuggestions,
}) => {
  const handleQueryUpdate = queryToUpdate => {
    handleInputUpdate({ target: { value: queryToUpdate } })
  }

  const preQuerySuggestionsProps = {
    searchHistory,
    handleClearHistory,
    handleQueryUpdate,
    fromPreQuerySuggestions: true,
  }

  const searchBarInteraction = getExperiment(
    SEARCH_BAR_INTERACTION,
    experiments
  )
  const serpPreQuerySuggestionsPerTenantExperiment =
    useGetExperimentNamePerTenant(
      SERP_PRE_QUERY_SUGGESTIONS_PER_TENANT_EXPERIMENT
    )

  const experimentPlaceholderText = displayCurrentOrRebrandingCOMExperimentText(
    experiments,
    {
      formal: 'Was brauchen Sie heute?',
      informal: 'Was brauchst du heute?',
    }
  )

  const defaultPlaceholderText =
    experimentPlaceholderText || i18n._('general.label.placeholder.search')
  return (
    <div
      className={classNames(`o-SearchBox`, {
        'o-searchBarInteractionExperimentV1V3':
          searchBarInteraction.isEnabled &&
          [EXPERIMENT_VARIATION.V1, EXPERIMENT_VARIATION.V3].includes(
            searchBarInteraction.variant
          ),
      })}
    >
      <div
        className={classNames(`o-PreQuerySuggestLayer__wrapper`, {
          'u-hidden': query,
        })}
        aria-hidden="true"
        data-clientside-hook="pqsl_wrapper-desktop"
      >
        {shouldRenderPqsl({
          tenant,
          pageType,
          preQuerySuggestionsExperimentEnabled:
            usePreQuerySuggestionsExperimentEnabledAndNotDefault(experiments),
        }) && (
          <PreQuerySuggestions
            hasSearchHistory={Boolean(searchHistory?.length)}
          >
            <SearchHistory {...preQuerySuggestionsProps} />
          </PreQuerySuggestions>
        )}

        {!EXCLUDED_PAGE_TYPES.includes(pageType) && (
          <Experiment
            name={serpPreQuerySuggestionsPerTenantExperiment}
            preventActivation
          >
            {({ isEnabled, variant }) => {
              if (isEnabled && variant !== EXPERIMENT_VARIATION.DEFAULT) {
                return (
                  <PreQuerySuggestionsExtended
                    hasSearchHistory={Boolean(searchHistory?.length)}
                    topBrands={variant === EXPERIMENT_VARIATION.V3}
                    topCategories={variant === EXPERIMENT_VARIATION.V2}
                  >
                    <SearchHistory {...preQuerySuggestionsProps} />
                  </PreQuerySuggestionsExtended>
                )
              }
            }}
          </Experiment>
        )}
      </div>

      <Form
        action={formAction}
        name="search-box"
        method="get"
        data-clientside-hook="chargeUrlSearchParams"
        onSubmit={handleSubmit}
      >
        <InputWithButton
          type="search"
          name="query"
          buttonType="submit"
          data-clientside-hook="track-apply"
          buttonLabel={i18n._('general.button.label.search')}
          placeholder={getPlaceholderText({
            tenant,
            defaultPlaceholderText,
            i18n,
          })}
          inputId={inputId}
          onChange={handleInputUpdate}
          onClear={handleClear}
          onKeyUp={handleKeyUp}
          value={displayedValue}
          icon={<IconSearch />}
          disableAutoCompleteAndFriends
          mediumViewIconOnly
          grayInputBg
        />
      </Form>

      <SearchBoxList
        query={query}
        displayResults={displayResults}
        products={products}
        redirectData={redirectData}
        focusedIndex={focusedIndex}
        formAction={formAction}
        handleShowAll={handleShowAll}
        handleItemClick={handleItemClick}
        hideResults={hideResults}
        handleValueChange={handleValueChange}
        withHighlightedProduct={withHighlightedProduct}
        querySuggestions={querySuggestions}
      />
    </div>
  )
}

SearchBoxDesktop.propTypes = {
  ...SearchHistory.propTypes,
  i18n: i18nType.isRequired,
  // comes from SearchBoxContainer
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  query: SearchBoxList.propTypes.query,
  inputId: string.isRequired,
  formAction: string.isRequired,
  displayResults: SearchBoxList.propTypes.displayResults,
  products: SearchBoxList.propTypes.products,
  redirectData: SearchBoxList.propTypes.redirectData,
  handleItemClick: SearchBoxList.propTypes.handleItemClick,
  handleInputUpdate: func,
  handleClearHistory: func,
  handleClear: func,
  handleKeyUp: func,
  handleSubmit: func,
  handleShowAll: func,
  focusedIndex: number,
  displayedValue: string,
  hideResults: func,
  handleValueChange: func,
  pageType: oneOf(PAGE_TYPES),
  searchBannerRef: shape({}),
  withHighlightedProduct: bool,
  querySuggestions: SearchBoxList.propTypes.querySuggestions,
}

SearchBoxDesktop.defaultProps = {
  ...SearchHistory.defaultProps,
  query: SearchBoxList.defaultProps.query,
  displayResults: SearchBoxList.defaultProps.displayResults,
  products: SearchBoxList.defaultProps.products,
  redirectData: SearchBoxList.defaultProps.redirectData,
  handleItemClick: SearchBoxList.defaultProps.handleItemClick,
  handleInputUpdate: null,
  handleClearHistory: null,
  handleClear: null,
  handleKeyUp: null,
  handleSubmit: null,
  handleShowAll: null,
  focusedIndex: -1,
  displayedValue: '',
  hideResults: null,
  handleValueChange: null,
  pageType: null,
  searchBannerRef: null,
  withHighlightedProduct: false,
  querySuggestions: SearchBoxList.defaultProps.querySuggestions,
}

export default withI18n()(SearchBoxDesktop)
